/**
 * EVENTSFLO-CREW-PWA-DOCUMENT-UPLOAD-MVP-V1
 *
 * Self-upload Documents panel for the ACTIVE Crew PWA (`/crew` route,
 * authenticated via JWT). Talks to the new `/api/crew/me/*` endpoints that
 * force the upload onto the caller's own staff_id and tenant.
 *
 * UX:
 *   1. Required document types render as cards with a status chip
 *      (Missing / Pending / Verified / Rejected).
 *   2. Tapping "Upload" opens a sheet with bilingual EN/FR guidance and
 *      two big buttons: "Take photo" (rear camera capture) and "Choose
 *      file". Both are mobile-friendly native file inputs.
 *   3. After file selection, a preview is shown. Crew confirms or retakes.
 *   4. Submit posts a multipart form to `/api/crew/me/documents` and
 *      refreshes the list.
 *
 * Security:
 *   - Backend forces staff_id from JWT, so the UI cannot upload for
 *     another crew member even with a tampered request.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertCircle,
  Camera,
  CheckCircle2,
  Clock,
  FileImage,
  FilePlus2,
  Loader2,
  RefreshCw,
  Upload,
  XCircle,
} from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";
import api, { extractErrorMessage } from "../../lib/api";
import { useI18n } from "../../i18n/I18nProvider";

// Status configuration is keyed by status code; labels are resolved at
// render time via the i18n hook to avoid hard-coding English strings.
const STATUS_CONFIG = {
  missing: { i18nKey: "crewPortal.selfDocuments.statusMissing", icon: AlertCircle, variant: "secondary" },
  pending: { i18nKey: "crewPortal.selfDocuments.statusPendingVerification", icon: Clock, variant: "default" },
  verified: { i18nKey: "crewPortal.selfDocuments.statusVerified", icon: CheckCircle2, variant: "default" },
  rejected: { i18nKey: "crewPortal.selfDocuments.statusRejectedReupload", icon: XCircle, variant: "destructive" },
};

// Display order + i18n display names. The backend always returns the
// authoritative list of document_types — this map only enriches the UI.
const TYPE_DISPLAY = {
  national_id_front: "ID Front",
  national_id_back: "ID Back",
  passport: "Passport",
  cv: "CV / Resume",
  utility_bill: "Utility Bill (proof of address)",
  address_support_document: "Proof of Address",
  health_insurance: "Health Insurance",
  carte_vitale: "Carte Vitale",
};

const ACCEPT_MIME = "image/jpeg,image/png,image/webp,application/pdf";

export default function CrewSelfDocumentsPanel({ "data-testid": testId }) {
  const { t } = useI18n();
  const [docTypes, setDocTypes] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState(null); // {code, name, id}
  const [pendingFile, setPendingFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: types }, { data: docs }] = await Promise.all([
        api.get("/crew/me/document-types"),
        api.get("/crew/me/documents"),
      ]);
      setDocTypes(types?.document_types || []);
      setSummaries(docs?.summaries || []);
    } catch (e) {
      toast.error(extractErrorMessage(e) || t("crewPortal.selfDocuments.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { refresh(); }, [refresh]);

  const summaryByCode = useMemo(() => {
    const m = {};
    for (const s of summaries) m[s.document_type_code] = s;
    return m;
  }, [summaries]);

  const closeUploader = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPendingFile(null);
    setActiveType(null);
  }, [previewUrl]);

  const onFilePicked = (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!f) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPendingFile(f);
    if (f.type.startsWith("image/")) {
      setPreviewUrl(URL.createObjectURL(f));
    } else {
      setPreviewUrl(null);
    }
  };

  const submitUpload = async () => {
    if (!pendingFile || !activeType) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      fd.append("document_type_code", activeType.code);
      const { data } = await api.post("/crew/me/documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("crewPortal.uploadPendingVerification", { name: TYPE_DISPLAY[activeType.code] || activeType.name }));
      closeUploader();
      await refresh();
      return data;
    } catch (e) {
      toast.error(extractErrorMessage(e) || t("crewPortal.selfDocuments.uploadFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4 pb-6" data-testid={testId || "crew-self-documents-panel"}>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">{t("crewPortal.selfDocuments.title")}</h2>
          <p className="text-xs text-muted-foreground">
            {t("crewPortal.selfDocuments.subtitle")}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
          {t("crewPortal.selfDocuments.refresh")}
        </Button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t("crewPortal.selfDocuments.loadingDots")}
        </div>
      )}

      {!loading && docTypes.length === 0 && (
        <Card>
          <CardContent className="text-sm text-muted-foreground py-6 text-center">
            {t("crewPortal.selfDocuments.noTypesConfigured")}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3" data-testid="crew-self-documents-list">
        {docTypes.map((dt) => {
          const code = dt.code;
          const summary = summaryByCode[code];
          const status = summary?.status || "missing";
          const meta = STATUS_CONFIG[status] || STATUS_CONFIG.missing;
          const Icon = meta.icon;
          const display = TYPE_DISPLAY[code] || dt.name || code;
          return (
            <Card key={code} data-testid={`crew-self-doc-card-${code}`}>
              <CardContent className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate">{display}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant={meta.variant} className="gap-1 text-[10px]">
                      <Icon className="h-3 w-3" /> {t(meta.i18nKey)}
                    </Badge>
                    {summary?.history_count > 1 && (
                      <span className="text-[10px] text-muted-foreground">
                        {t("crewPortal.selfDocuments.versionsLabel", {
                          version: summary.version,
                          count: summary.history_count,
                        })}
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => { setActiveType({ code, name: dt.name, id: dt.id }); }}
                  data-testid={`crew-self-doc-upload-btn-${code}`}
                >
                  <Upload className="h-3.5 w-3.5 mr-1" />
                  {status === "missing"
                    ? t("crewPortal.selfDocuments.uploadAction")
                    : t("crewPortal.selfDocuments.replaceAction")}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!activeType} onOpenChange={(v) => { if (!v && !submitting) closeUploader(); }}>
        <DialogContent className="max-w-md" data-testid="crew-self-doc-upload-sheet">
          <DialogHeader>
            <DialogTitle>
              {activeType ? t("crewPortal.selfDocuments.uploadTitle", { name: TYPE_DISPLAY[activeType.code] || activeType.name }) : ""}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Document is sent for admin verification — it stays Pending until reviewed.
            </DialogDescription>
          </DialogHeader>

          {!pendingFile ? (
            <div className="space-y-4">
              <div className="rounded-md border bg-muted/30 p-3 text-xs space-y-2">
                <div>
                  <strong>EN:</strong> Take a clear photo of the document only. Use good light, avoid glare/shadows, place the document flat, and make sure all edges are visible.
                </div>
                <div>
                  <strong>FR:</strong> Prenez une photo nette du document uniquement. Utilisez une bonne lumière, évitez les reflets/ombres, posez le document à plat et assurez-vous que tous les bords sont visibles.
                </div>
              </div>

              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={onFilePicked}
                data-testid="crew-self-doc-camera-input"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT_MIME}
                className="hidden"
                onChange={onFilePicked}
                data-testid="crew-self-doc-file-input"
              />

              <div className="grid grid-cols-2 gap-3">
                <Button
                  size="lg"
                  className="h-20 flex-col gap-1"
                  onClick={() => cameraInputRef.current?.click()}
                  data-testid="crew-self-doc-take-photo-btn"
                >
                  <Camera className="h-5 w-5" />
                  Take photo
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="h-20 flex-col gap-1"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="crew-self-doc-choose-file-btn"
                >
                  <FilePlus2 className="h-5 w-5" />
                  Choose file
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4" data-testid="crew-self-doc-preview">
              <div className="rounded-md border bg-muted/20 p-2 flex items-center justify-center min-h-[180px]">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt={t("selfDocuments.previewAlt")}
                    className="max-h-72 object-contain"
                  />
                ) : (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
                    <FileImage className="h-5 w-5" />
                    {pendingFile.name} ({Math.round(pendingFile.size / 1024)} KB)
                  </div>
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                {pendingFile.name} · {Math.round(pendingFile.size / 1024)} KB
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    if (previewUrl) URL.revokeObjectURL(previewUrl);
                    setPendingFile(null);
                    setPreviewUrl(null);
                  }}
                  disabled={submitting}
                  data-testid="crew-self-doc-retake-btn"
                >
                  Retake / choose another
                </Button>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="ghost"
              onClick={closeUploader}
              disabled={submitting}
              data-testid="crew-self-doc-cancel-btn"
            >
              Cancel
            </Button>
            {pendingFile && (
              <Button
                onClick={submitUpload}
                disabled={submitting}
                data-testid="crew-self-doc-submit-btn"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Uploading…</>
                ) : (
                  <><Upload className="h-4 w-4 mr-1" /> Upload</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
